import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { getAccessToken, isSessionVerified } from '@/shared/lib/tokenStorage';

interface AuthState {
  /**
   * Whether the session has cleared every gate, OTP included.
   *
   * This is deliberately NOT the same question as "is there a token". Between
   * login and OTP verification a token exists but the director is not yet
   * authenticated, and the route guard must reject that state.
   */
  isAuthenticated: boolean;
  /** Explains an involuntary logout on the logout screen; empty when the
   *  director left on purpose. */
  errorMessage: string;
}

/**
 * Seeded from storage so a reload does not bounce a signed-in director to the
 * logout screen before the first render.
 *
 * Both halves are required. A token exists from `/director/login` onwards —
 * i.e. while the director is still sitting on the OTP screen — so seeding from
 * the token alone would let a reload there return a fully authenticated
 * session and skip the second factor. The verified marker is written only once
 * OTP has actually passed.
 */
const initialState: AuthState = {
  isAuthenticated: getAccessToken() !== null && isSessionVerified(),
  errorMessage: '',
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    authenticated(state) {
      state.isAuthenticated = true;
      state.errorMessage = '';
    },
    signedOut(state, action: PayloadAction<string | undefined>) {
      state.isAuthenticated = false;
      state.errorMessage = action.payload ?? '';
    },
  },
  // Slice-local selectors: they resolve against the mount point without
  // importing RootState, which would create an app -> feature -> app cycle.
  selectors: {
    selectIsAuthenticated: (state) => state.isAuthenticated,
    selectAuthErrorMessage: (state) => state.errorMessage,
  },
});

export const { authenticated, signedOut } = authSlice.actions;
export const { selectIsAuthenticated, selectAuthErrorMessage } = authSlice.selectors;
export const authReducer = authSlice.reducer;
